import { nanoid } from 'nanoid';
import 'regenerator-runtime/runtime';
import { PDFDocument } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';

import {
  updatePDF,
  signDocument,
  addFormFields,
  removeImageFromS3Bucket,
  generateRealtimeFilePreview,
  mapTemplatePlaceholders,
} from '../services/file';
import APP_CONFIG from '../config';
import { blobToURL } from '../utils/file';
import {
  dateFormatOptionsList,
  supportedColumnsFromMonday,
} from '../config/common';
import { filterGhostEmailColumns } from '@/utils/common';
import { DEFAULT_FONT_COLOR } from '@/constants/constants';

export const FileContext = createContext();

export const FileContextProvider = ({ children }) => {
  const { t } = useTranslation();
  const [eachFile, setFile] = useState(null);
  const [userId, setUserId] = useState('');
  const [itemId, setItemId] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [boardId, setBoardId] = useState(null);
  const [subItems, setSubItems] = useState(null);
  const [boardName, setBoardName] = useState('');
  const [boardData, setBoardData] = useState(null);
  const [isDeleted, setIsDeleted] = useState(false);
  const [headerData, setHeaderData] = useState(null);
  const [envelopeId, setEnvelopeId] = useState(null);
  const [toastMessage, triggerToast] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [envelopeData, setEnvelopeData] = useState({});
  const [previewFile, setPreviewFile] = useState(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [signatureMode, setSignatureMode] = useState([]);
  const [hasPageLoaded, setHasPageLoaded] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const [addedFormFields, setAddedFormFields] = useState([]);
  const [previewFlowData, setPreviewFlowData] = useState({});
  const [animateTooltip, setAnimateTooltip] = useState(false);
  const [isItemDragging, setIsItemDragging] = useState(false);
  const [pagesAddedByUser, setPagesAddedByUser] = useState([]);
  const [generateFlowData, setGenerateFlowData] = useState({});
  const [pageDeleteIndex, setPageDeleteIndex] = useState(false);
  const [pdfDefaultHeight, setPdfDefaultHeight] = useState(null);
  const [columnValuesMap, setColumnValuesMap] = useState(new Map());
  const [showLineItemModal, setShowLineItemModal] = useState(false);
  const [successToastMessage, triggerSuccessToast] = useState(null);
  const [lineItemsRowHeights, setLineItemsRowHeights] = useState({});
  const [imagesToRemoveArray, setImagesToRemoveArray] = useState([]);
  const [triggerHeaderButton, setTriggerHeaderButton] = useState(null);
  const [isEditorSaveLoading, setIsEditorSaveLoading] = useState(false);
  const [updateSignOrderList, setUpdateSignOrderList] = useState(false);
  const [isSenderSigningPage, setIsSenderSigningPage] = useState(false);
  const [isPreviewSaveLoading, setIsPreviewSaveLoading] = useState(false);
  const [hasPreviewPageLoaded, setHasPreviewPageLoaded] = useState(false);
  const [isReceiverSigningPage, setIsReceiverSigningPage] = useState(false);
  const [displayOnboardingFlow, setDisplayOnboardingFlow] = useState(false);
  const [updateTemplateAndFileFields, setUpdateTemplateAndFileFields] =
    useState(false);
  const [pageScale, setPageScale] = useState({ value: '1.5', label: '150%' });
  const [isTemplate, setIsTemplate] = useState({
    status: false,
    templateId: null,
  });
  const [showSignatureOrderModal, setShowSignateOrderModal] = useState(false);
  const [showCKEditorEntryConfirmation, setShowCKEditorEntryConfirmation] =
    useState(false);
  const [hasChangedAddedFormFields, setAddedFormFieldsChanged] =
    useState(false);
  const [pdfIndividualPageDetails, setPdfIndividualPageDetails] =
    useState(null);
  const [editorFlowData, setEditorFlowData] = useState({
    isAdhoc: false,
  });
  const [initialFields, setInitialFields] = useState([]);
  const [pricingPlan, setPricingPlan] = useState(APP_CONFIG.pricingPlans.free);
  const [slug, setSlug] = useState('');
  const [signFlowData, setSignFlowData] = useState({
    showDeclineSignaturePopup: false,
    showDeclinedPDFModal: false,
    showPayNowDialog: false,
    showErrorSignaturePopup: false,
  });
  const [signersSettings, setSignersSettings] = useState({
    isSigningOrderRequired: false,
    signers: [],
  });
  const [activeHeaderButton, setActiveHeaderButton] = useState({
    value: 'get-started',
    text: 'Get Started',
    color: 'blue',
  });
  const [activeHeaderButtonSignAnywhere, setActiveHeaderButtonSignAnywhere] =
    useState({
      value: 'get-started',
      text: 'Get Started',
      color: 'blue',
    });
  const [pdfDimensions, setPdfDimensions] = useState({
    width: null,
    height: null,
    scale: null,
  });
  const [showSignaturePad, setShowSignaturePad] = useState({
    show: false,
    item: null,
    action: null,
  });
  const [draggableFieldsData, setDraggableFieldsData] = useState({
    'signature-field': { height: 26.66, width: 183.33 },
    'initials-field': { height: 26.66, width: 183.33 },
    checkbox: { height: 25, width: 25 },
    'text-box': { height: 18.33, width: 150 },
    'sd-dropdown': { height: 18.33, width: 150, fontSize: 11 },
    'sign-date': { height: 26.66, width: 183.33, fontSize: 11 },
    'line-item': { height: '100%', width: 564 },
    'sd-file': { height: 90, width: 200 },
  });
  const [
    isAdhocWithSignatureFieldAutoAssign,
    setIsAdhocWithSignatureFieldAutoAssign,
  ] = useState(false);
  const [paymentFieldAdded, setPaymentFieldAdded] = useState(false);

  const documentRef = useRef();
  const currentSelectedItem = useRef(null);

  const {
    refetch: updatePreviewFile,
    isLoading: isPreviewLoading,
    isFetching: isPreviewFetching,
  } = useQuery(
    [
      'generate-realtime-preview-file',
      envelopeId,
      itemId,
      envelopeData.activeFileId,
    ],
    () =>
      generateRealtimeFilePreview(
        envelopeId,
        envelopeData.activeFileId,
        itemId,
        addedFormFields,
      ),
    {
      enabled: false,
      onSuccess: (res) => {
        if (addedFormFields[currentSelectedItem.current]) {
          addedFormFields[currentSelectedItem.current].isActive = false;
          setAddedFormFields([...addedFormFields]);
        }
        currentSelectedItem.current = null;
        setPreviewFile(res?.data?.file);
      },
      refetchOnWindowFocus: false,
    },
  );

  const { mutate: addSignature, isLoading: isAddingSign } = useMutation(
    signDocument,
    { mutationKey: 'signing-document' },
  );

  const { mutate: uploadUpdatedPDF, isLoading: pdfUpdating } =
    useMutation(updatePDF);

  const { mutate: saveFormFields } = useMutation(addFormFields);

  const { mutate: addTemplateFields } = useMutation(mapTemplatePlaceholders);

  const handleAnimateTooltip = () => {
    setAnimateTooltip(true);
    setTimeout(() => {
      setAnimateTooltip(false);
    }, 800);
  };

  const handleRenderSuccess = () => {
    if (!hasPageLoaded) {
      setHasPageLoaded(true);
    }
  };

  const handleEditSignature = (item) => {
    enableSignaturePad({ show: true, item, action: 'edit' });
  };

  const handleDeleteSignature = (item) => {
    const idx = addedFormFields?.findIndex((field) => field.id === item.id);
    delete addedFormFields[idx].image;
    setAddedFormFields([...addedFormFields]);
  };

  const addPlaceholderForAcroFields = (item) => {
    setAddedFormFieldsChanged(true);
    setAddedFormFields((prev) => [...prev, item]);
  };

  const addPlaceHolder = async ({ x, y, item, update }) => {
    setAddedFormFieldsChanged(true);
    // set setPaymentFieldAdded to true if payment field is added
    if (item.id === 'sd-payment') {
      setPaymentFieldAdded(true);
    }
    // here x is coordinates in pdf in xaxis
    // here y is coordinates in pdf in y axis considering entire pdf
    const allPageHeights = pdfIndividualPageDetails.map(
      (x) => x.height * pdfDimensions.scale,
    );
    const updatedY = envelopeData.fileLinkTop
      ? y - 85 * pdfDimensions.scale
      : y;
    let pageNumber = 1;
    let yPositionInPage = updatedY;

    for (let i = 0; i < allPageHeights.length; i++) {
      const pageHeight = allPageHeights[i];
      const adjustedPageHeight = pageHeight + 16;

      if (yPositionInPage > adjustedPageHeight) {
        pageNumber++;
        yPositionInPage -= adjustedPageHeight;
      } else {
        break; // Exit the loop if the yPositionInPage is within this page
      }
    }

    let scaledX = x / pdfDimensions.scale;
    let scaledY = yPositionInPage / pdfDimensions.scale;

    // this is not mandatory  but a safety net if dragging cannot reposition to grid on snap
    const FieldsInPage = addedFormFields
      .filter((x) => x.formField.pageNumber === pageNumber)
      .map((y) => y.placeholder);
    const allXCoordinates = FieldsInPage.map((coordinate) => coordinate.x);
    const allYCoordinates = FieldsInPage.map((coordinate) => coordinate.y);
    let closestX;
    if (allXCoordinates && allXCoordinates.length > 0) {
      closestX = allXCoordinates.reduce((prev, curr) =>
        Math.abs(curr - scaledX) < Math.abs(prev - scaledX) ? curr : prev,
      );
    } else {
      closestX = null;
    }
    let closestY;
    if (allYCoordinates && allYCoordinates.length > 0) {
      closestY = allYCoordinates.reduce((prev, curr) =>
        Math.abs(curr - scaledY) < Math.abs(prev - scaledY) ? curr : prev,
      );
    } else {
      closestY = null;
    }
    if (closestX || closestY) {
      if (Math.abs(closestX - scaledX) > Math.abs(closestY - scaledY)) {
        if (Math.abs(closestY - scaledY) < 7) {
          scaledY = closestY;
        }
      } else {
        if (Math.abs(closestX - scaledX) < 7) {
          scaledX = closestX;
        }
      }
    }

    const activeFile =
      envelopeData?.activeFile?.file ||
      generateFlowData?.pdfFileData?.updated_file;
    const pdfDoc = await PDFDocument.load(activeFile);
    const pages = pdfDoc.getPages();
    const currentPage = pages[pageNumber - 1];
    const pdfBox = currentPage.getSize();
    const rotationAngle = currentPage.getRotation().angle;
    const effectiveHeight =
      rotationAngle === 90 || rotationAngle === 270
        ? pdfBox.width
        : pdfBox.height;
    const newY = effectiveHeight - (scaledY % effectiveHeight);
    if (update) {
      const index = addedFormFields.findIndex((field) => field.id === item.id);
      if (index > -1) {
        const updatingFields = [...addedFormFields];
        updatingFields[index] = {
          ...updatingFields[index],
          placeholder: {
            x: scaledX,
            y: scaledY,
          },
          formField: {
            pageNumber: pageNumber,
            pageIndex: pageNumber - 1,
            coordinates: {
              x: scaledX,
              y: newY,
            },
          },
        };

        setAddedFormFields(updatingFields);
      }
      setIsItemDragging(false);
      return;
    }

    // update focus selection
    addedFormFields.forEach((item) => (item.isActive = false));
    currentSelectedItem.current = addedFormFields.length;
    const itemDimensions = draggableFieldsData[item.id];
    const id =
      item.itemId === 'file'
        ? `${item.itemId}-${nanoid()}`
        : `${item.title}-${nanoid()}`;

    setAddedFormFields((prev) => [
      ...prev,
      {
        id,
        itemId: item.id,
        title: item.title,
        type: item.type,
        height: itemDimensions?.height || item.height,
        width: itemDimensions?.width || item.width,
        fontSize: itemDimensions?.fontSize || 11,
        noPadding: true,
        placeholder: {
          x: scaledX,
          y: scaledY,
        },
        isActive: true,
        dir: 'ltr',
        formField: {
          pageNumber: pageNumber,
          pageIndex: pageNumber - 1,
          coordinates: {
            x: scaledX,
            y: newY,
          },
        },
        ...(item.id === 'sd-datepicker'
          ? { format: dateFormatOptionsList[0] }
          : {}),
        ...(item.id === 'sign-date'
          ? { dateFormat: dateFormatOptionsList[0], requiresSigner: true }
          : {}),
        ...(item.id === 'checkbox' ? { signer: item.signer } : {}),
        ...(item.id === 'line-item' ? { subItemSettings: subItems } : {}),
        ...(item.type === 'numeric'
          ? { formattedValue: item.formattedValue }
          : {}),
        ...(item.type === 'date'
          ? { dateFormat: dateFormatOptionsList[0] }
          : {}),
        fontStyle: {
          bold: false,
          italics: false,
          alignment: 'left',
          fontSize: 11,
          color: DEFAULT_FONT_COLOR,
        },
      },
    ]);
  };

  const addSignatureFieldInSignPage = async ({ x, y, item, update }) => {
    setAddedFormFieldsChanged(true);
    // here x is coordinates in pdf in xaxis
    // here y is coordinates in pdf in y axis considering entire pdf
    const allPageHeights = pdfIndividualPageDetails.map(
      (x) => x.height * pdfDimensions.scale,
    );
    const updatedY = envelopeData.fileLinkTop
      ? y - 85 * pdfDimensions.scale
      : y;
    let pageNumber = 1;
    let yPositionInPage = updatedY;

    for (let i = 0; i < allPageHeights.length; i++) {
      const pageHeight = allPageHeights[i];
      const adjustedPageHeight = pageHeight + 16;

      if (yPositionInPage > adjustedPageHeight) {
        pageNumber++;
        yPositionInPage -= adjustedPageHeight;
      } else {
        break; // Exit the loop if the yPositionInPage is within this page
      }
    }

    let scaledX = x / pdfDimensions.scale;
    let scaledY = yPositionInPage / pdfDimensions.scale;

    // this is not mandatory  but a safety net if dragging cannot reposition to grid on snap
    const FieldsInPage = addedFormFields
      .filter((x) => x.formField.pageNumber === pageNumber)
      .map((y) => y.placeholder);
    const allXCoordinates = FieldsInPage.map((coordinate) => coordinate.x);
    const allYCoordinates = FieldsInPage.map((coordinate) => coordinate.y);
    let closestX;
    if (allXCoordinates && allXCoordinates.length > 0) {
      closestX = allXCoordinates.reduce((prev, curr) =>
        Math.abs(curr - scaledX) < Math.abs(prev - scaledX) ? curr : prev,
      );
    } else {
      closestX = null;
    }
    let closestY;
    if (allYCoordinates && allYCoordinates.length > 0) {
      closestY = allYCoordinates.reduce((prev, curr) =>
        Math.abs(curr - scaledY) < Math.abs(prev - scaledY) ? curr : prev,
      );
    } else {
      closestY = null;
    }
    if (closestX || closestY) {
      if (Math.abs(closestX - scaledX) > Math.abs(closestY - scaledY)) {
        if (Math.abs(closestY - scaledY) < 7) {
          scaledY = closestY;
        }
      } else {
        if (Math.abs(closestX - scaledX) < 7) {
          scaledX = closestX;
        }
      }
    }

    const activeFile = signFlowData?.activeFile?.file;
    const pdfDoc = await PDFDocument.load(activeFile);
    const pages = pdfDoc.getPages();
    const currentPage = pages[pageNumber - 1];
    const pdfBox = currentPage.getSize();
    const rotationAngle = currentPage.getRotation().angle;
    const effectiveHeight =
      rotationAngle === 90 || rotationAngle === 270
        ? pdfBox.width
        : pdfBox.height;
    const newY = effectiveHeight - (scaledY % effectiveHeight);

    // update focus selection
    addedFormFields.forEach((item) => (item.isActive = false));
    currentSelectedItem.current = addedFormFields.length;
    const itemDimensions = draggableFieldsData[item.id];

    const formFieldData = {
      id: `${item.title}-${nanoid()}`,
      itemId: item.id,
      title: item.title,
      type: item.id,
      height: 40,
      width: 60,
      fontSize: itemDimensions?.fontSize || 11,
      noPadding: true,
      placeholder: {
        x: scaledX,
        y: scaledY,
      },
      isActive: true,
      formField: {
        pageNumber: pageNumber,
        pageIndex: pageNumber - 1,
        coordinates: {
          x: scaledX,
          y: newY,
        },
      },
    };
    setAddedFormFields((prev) => [...prev, formFieldData]);
    return formFieldData;
  };

  const removePlaceholder = (item) => {
    const isGenerateFlow = !!generateFlowData?.pdfFileData?.updated_file;
    const signFlow = !!signFlowData?.activeFile?.file;
    !isGenerateFlow && !signFlow && setUpdateSignOrderList(true);
    const index = addedFormFields.findIndex((field) => field.id === item.id);
    const updatedFields = [...addedFormFields];
    if (item.itemId === 'sd-image' && item.imageId) {
      setImagesToRemoveArray((prev) => {
        return [...prev, item.imageId];
      });
    }
    if (item.itemId === 'sd-payment') {
      setPaymentFieldAdded(false);
    }
    updatedFields.splice(index, 1);
    setAddedFormFields([...updatedFields]);
    currentSelectedItem.current = null;
    setAddedFormFieldsChanged(true);
  };

  const downloadFile = async (name = 'file.pdf') => {
    let url = eachFile;
    const pdfDoc = await PDFDocument.load(url);
    const pages = pdfDoc.getPages();
    pdfDoc.registerFontkit(fontkit);

    if (addedFormFields.length) {
      addedFormFields.forEach(async (field) => {
        const currentPage = pages[field?.formField?.pageIndex];

        if (field?.image) {
          const pngImage = await pdfDoc.embedPng(field?.image?.src);
          currentPage.drawImage(pngImage, {
            x: field?.formField.coordinates.x,
            y: field?.formField.coordinates.y,
            width: field?.image.width,
            height: field?.image.height,
          });
        } else {
          const paddingX = -6;
          const fontSize = field?.fontSize || 11;
          const scalingFactor = 0.75;
          const paddingY = 22.8 - (fontSize - 11) * scalingFactor;
          currentPage.drawText(field.value, {
            x: field.formField.coordinates.x + paddingX,
            y: field.formField.coordinates.y + paddingY,
            size: fontSize,
          });
        }
      });
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([new Uint8Array(pdfBytes)]);
      url = await blobToURL(blob);
    }
    var link = document.createElement('a');
    link.download = name;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const updatePdfInBE = ({
    isTemporaryFile = false,
    fileId,
    isUserSavingFields = false,
  }) => {
    if (!envelopeData?.isAdhoc) {
      const data = envelopeData?.pdfFileData?.find((x) => x.id === fileId);
      if (
        (data.filetype !== 'docx' || data.filetype !== 'doc') &&
        data?.original_file !== data?.updated_file
      ) {
        uploadUpdatedPDF(
          {
            file: data?.updated_file,
            fileData: data,
            isTemporaryFile,
            fileHasBeenUpdated: data?.original_file !== data?.updated_file,
          },
          {
            onSuccess: () => {
              if (isTemporaryFile) {
                !isUserSavingFields && updatePreviewFile();
              }
            },
          },
        );
      } else {
        !isUserSavingFields && updatePreviewFile();
      }
    } else {
      !isUserSavingFields && updatePreviewFile();
    }
  };

  const validateSaveFormFields = () => {
    const fieldsToValidate = {
      textbox: addedFormFields.filter(
        (field) =>
          field.itemId === APP_CONFIG.form_fields.textBox.id &&
          field.saveIn &&
          !field.column,
      ),
      dropdown: addedFormFields.filter(
        (field) =>
          field.itemId === APP_CONFIG.form_fields.dropdown.id &&
          !field.column &&
          !(Array.isArray(field.options) && field.options.length > 0),
      ),
      link: addedFormFields.filter(
        (field) =>
          field.itemId === APP_CONFIG.form_fields.link.id &&
          !field?.urlData?.url,
      ),
      payment: addedFormFields.filter(
        (field) =>
          (field.itemId === APP_CONFIG.form_fields.payment.id &&
            !field?.currency) ||
          (field.itemId === APP_CONFIG.form_fields.payment.id &&
            !field?.amountColumn),
      ),
    };
    return fieldsToValidate;
  };

  const handleSaveFormFields = (cb) => {
    if (addedFormFields.length)
      addedFormFields.forEach((item) => delete item?.isActive);

    const placeHolderData = envelopeData?.placeHolders || [];

    const { cleanedPlaceholders, cleanedSignerSettings } =
      filterGhostEmailColumns(placeHolderData, signersSettings, boardData);

    if (isTemplate?.status) {
      addTemplateFields(
        {
          signer_order_settings: cleanedSignerSettings,
          placeholders: cleanedPlaceholders,
          templateId: isTemplate.templateId,
          isV3: true,
        },
        {
          onSuccess: (res) => {
            if (res?.message) {
              cb?.(res);
              setAddedFormFieldsChanged(false);
              setIsEditorSaveLoading(false);
              setIsPreviewSaveLoading(false);
            }
          },
        },
      );

      if (updateTemplateAndFileFields) {
        saveFormFields(
          {
            signer_order_settings: cleanedSignerSettings,
            placeholders: cleanedPlaceholders,
            envelope_id: envelopeId,
            item_id: itemId,
          },
          {
            onSuccess: (res) => {
              if (res?.message) {
                cb?.(res);
                setAddedFormFieldsChanged(false);
                setIsEditorSaveLoading(false);
                setIsPreviewSaveLoading(false);
              }
            },
          },
        );
      }
      // Update the original pdf in BE
      envelopeData?.pdfFileData?.forEach((file) => {
        updatePdfInBE({ fileId: file.id, isUserSavingFields: true });
      });

      imagesToRemoveArray.forEach((imageId) => {
        removeImageFromS3Bucket(imageId);
      });
    } else {
      saveFormFields(
        {
          signer_order_settings: cleanedSignerSettings,
          placeholders: cleanedPlaceholders,
          envelope_id: envelopeId,
          item_id: itemId,
        },
        {
          onSuccess: (res) => {
            if (res?.message) {
              cb?.(res);
              setAddedFormFieldsChanged(false);
              setIsEditorSaveLoading(false);
              setIsPreviewSaveLoading(false);
            }
          },
        },
      );

      // Update the original pdf in BE
      envelopeData?.pdfFileData?.forEach((file) => {
        updatePdfInBE({ fileId: file.id, isUserSavingFields: true });
      });

      imagesToRemoveArray.forEach((imageId) => {
        removeImageFromS3Bucket(imageId);
      });
    }
  };

  const handleTemplateFieldsV3 = (cb) => {
    if (addedFormFields.length)
      addedFormFields.forEach((item) => delete item?.isActive);

    const placeHolderData = envelopeData?.placeHolders || [];

    const { cleanedPlaceholders, cleanedSignerSettings } =
      filterGhostEmailColumns(placeHolderData, signersSettings, boardData);

    addTemplateFields(
      {
        signer_order_settings: cleanedSignerSettings,
        placeholders: cleanedPlaceholders,
        templateId: isTemplate.templateId,
        isV3: true,
      },
      {
        onSuccess: (res) => {
          if (res?.message) {
            cb?.(res);
            setAddedFormFieldsChanged(false);
            setIsEditorSaveLoading(false);
            setIsPreviewSaveLoading(false);
          }
        },
        onError: (err) => {
          setAddedFormFieldsChanged(false);
          setIsEditorSaveLoading(false);
          setIsPreviewSaveLoading(false);
        },
      },
    );
  };

  const handleSaveFormFieldsV3 = (cb) => {
    if (addedFormFields.length)
      addedFormFields.forEach((item) => delete item?.isActive);

    const placeHolderData = envelopeData?.placeHolders || [];

    const { cleanedPlaceholders, cleanedSignerSettings } =
      filterGhostEmailColumns(placeHolderData, signersSettings, boardData);

    if (signersSettings?.signers?.length === 0) {
      const extractedSigners = placeHolderData
        ?.map((x) => x?.signers_settings || [])
        ?.flat(1)
        ?.filter(Boolean);

      if (extractedSigners?.length > 0) {
        const uniqueSignersMap = new Map(
          extractedSigners.map((signer) => [signer.value, signer]),
        );

        const uniqueSignersArray = Array.from(uniqueSignersMap.values());

        if (uniqueSignersArray.length > 0) {
          setSignersSettings({
            isSigningOrderRequired: false,
            signers: uniqueSignersArray,
          });
        }
      }
    }

    saveFormFields(
      {
        signer_order_settings: cleanedSignerSettings,
        placeholders: cleanedPlaceholders,
        envelope_id: envelopeId,
        item_id: itemId,
        isV3: true,
      },
      {
        onSuccess: (res) => {
          if (res?.message) {
            cb?.(res);
            setAddedFormFieldsChanged(false);
            setIsEditorSaveLoading(false);
            setIsPreviewSaveLoading(false);
          }
        },
        onError: (err) => {
          setAddedFormFieldsChanged(false);
          setIsEditorSaveLoading(false);
          setIsPreviewSaveLoading(false);
        },
      },
    );

    // Update the original pdf in BE
    envelopeData?.pdfFileData?.forEach((file) => {
      updatePdfInBE({ fileId: file.id, isUserSavingFields: true });
    });

    imagesToRemoveArray.forEach((imageId) => {
      removeImageFromS3Bucket(imageId);
    });
  };

  const scrollDocumentY = async (pageIndex, y) => {
    const totalHeightAbove = pdfIndividualPageDetails
      ?.slice(0, pageIndex)
      ?.map((obj) => obj.height * pdfDimensions.scale)
      ?.reduce((a, b) => a + b, 0);
    documentRef.current?.scroll({
      top: totalHeightAbove + y * pdfDimensions.scale + 16 * pageIndex - 122, //16 is the margin between pages * total pages above, 122 is reduced so that the coordinate excludes the header and messages.
      behavior: 'smooth',
    });
  };

  const addSignatureImage = async (imgSrc, item, scaleFactor) => {
    const pdfDoc = await PDFDocument.load(signFlowData?.activeFile?.file);
    const pngImage = await pdfDoc.embedPng(imgSrc);
    const pngDims = pngImage.scale(scaleFactor);
    pdfDoc.registerFontkit(fontkit);
    const index = addedFormFields?.findIndex((field) => field.id === item.id);

    if (index > -1) {
      addedFormFields[index].image = {
        src: imgSrc,
        height: pngDims.height,
        width: pngDims.width,
      };
    }

    setAddedFormFields([...addedFormFields]);
    enableSignaturePad({ show: false, item: null, action: null });
  };

  const resizeSignature = async (item, dX, dY) => {
    const index = addedFormFields?.findIndex((field) => field.id === item.id);

    if (index > -1) {
      const updatedSignatureImage = addedFormFields[index].image;
      updatedSignatureImage.height += dY;
      updatedSignatureImage.width += dX;
    }

    setAddedFormFields([...addedFormFields]);
  };

  const getColumnWidth = (data) => {
    const columnItems =
      data?.[0]?.column_values?.slice(0, 4).map((x) => x.id) || [];
    const columnWidth = { 'item-name': 36 };
    for (const key of columnItems) {
      columnWidth[key] = 16;
    }
    return columnWidth;
  };

  const removeAnyUnusedSigners = () => {
    /*
    addedFormFields ---> holds all the fields for the active file for the current session.
    envelopeData.attachedFiles   ---> holds all the fields for all the file saved on the database.
    envelopeData.placeHolders    ---> holdes all the fields for the all the file for the current session.
    */

    /*
    STEPS TO GET ALL THE UNIQUE SIGNERS FOR THE CURRENT SESSION
    */

    const activeFileId = envelopeData?.activeFileId;
    const remoteFileToSignersMap = new Map();
    const activeFileToSignersMap = new Map();
    const placeHolderFileToSignerMap = new Map();

    // This loop maps all the signers attached to each file that is present on our database.
    for (const file of envelopeData?.attachedFiles) {
      const fileId = file._id;
      const signerIdMap = new Map();
      for (const field of file?.fields || []) {
        if (!field.signer) continue;
        signerIdMap.set(
          field.signer?.value || field.signer?.userId,
          field.signer,
        );
      }
      remoteFileToSignersMap.set(fileId, signerIdMap);
    }

    // This Section with loop will map all the signers present in each file for this editor session
    const editorSignerIdMap = new Map();
    for (let field of addedFormFields) {
      if (!field.signer) continue;

      field.signer = {
        ...field.signer,
        emailColumnId: field.signer.value,
      };
      editorSignerIdMap.set(field.signer.value, field.signer);
    }

    activeFileToSignersMap.set(activeFileId, editorSignerIdMap);
    // Section ends here

    for (const eachFile of envelopeData.placeHolders) {
      const fileId = envelopeData?.isAdhoc
        ? eachFile?.assetId
        : eachFile.fileId;

      const signerIdMap = new Map();
      for (const field of eachFile?.fields) {
        if (!field.signer) continue;
        signerIdMap.set(field.signer.value, field.signer);
      }
      placeHolderFileToSignerMap.set(fileId, signerIdMap);
    }

    placeHolderFileToSignerMap.set(
      activeFileId,
      activeFileToSignersMap.get(activeFileId),
    );

    /*
    THIS SECTION WITH LOOP WILL MAP ALL THE SIGNERS TO EACH FILE FOR THIS EDITOR SESSION.
    */

    for (const fileId of remoteFileToSignersMap.keys()) {
      const signerMap = remoteFileToSignersMap.get(fileId);
      if (
        !placeHolderFileToSignerMap.has(
          typeof fileId === 'number' ? fileId.toString() : fileId,
        )
      ) {
        placeHolderFileToSignerMap.set(fileId, signerMap);
      }
    }
    const uniqueEmailFields = [];

    for (const [_, innerMap] of placeHolderFileToSignerMap) {
      for (const innerValue of innerMap.keys()) {
        if (
          uniqueEmailFields.findIndex(
            (field) => (field?.value || field?.userId) === innerValue,
          ) === -1
        ) {
          uniqueEmailFields.push(innerMap.get(innerValue));
        }
      }
    }

    // allSigners = Array.from(uniqueEmailFields);

    const allSignerValues = uniqueEmailFields.map(
      (fld) => fld?.value || fld?.userId,
    );

    let currentSignerOrder = signersSettings?.signers || [];

    // filter all the unwanted signers from current signer order
    currentSignerOrder = currentSignerOrder.filter((sgnrOrdr) =>
      allSignerValues.includes(sgnrOrdr.emailColumnId),
    );

    for (let emailField of uniqueEmailFields) {
      if (
        currentSignerOrder.findIndex(
          (sgnr) =>
            sgnr.emailColumnId === (emailField?.value || emailField?.userId),
        ) === -1
      ) {
        delete emailField?.leftIcon;
        delete emailField?.value;
        currentSignerOrder.push({
          ...emailField,
        });
      }
    }

    setSignersSettings((prevSettings) => ({
      ...prevSettings,
      signers: currentSignerOrder,
    }));
  };

  const calculateRowHeightForEachCell = (lineItemId, table) => {
    const rowHeight = [];
    table.getRowModel().rows.forEach((row) => {
      const rowElement = document.getElementById(`row-${lineItemId}-${row.id}`);
      if (rowElement) {
        rowHeight.push(rowElement.offsetHeight);
      }
    });
    return rowHeight;
  };

  const updateLineItemsRowHeights = (lineItemId, table) => {
    const dynamicHeightsArray = calculateRowHeightForEachCell(
      lineItemId,
      table,
    );
    setLineItemsRowHeights((prev) => {
      return { ...prev, [lineItemId]: dynamicHeightsArray };
    });
  };

  const updateSubItems = useCallback(
    (data) => {
      const updatedHeader = data?.[0]?.board?.columns?.map((x) => {
        return { ...x, settings_str: JSON.parse(x.settings_str) };
      });

      const columnWidth = getColumnWidth(data);
      setHeaderData(updatedHeader);
      const defaultVal = {
        sum: {
          checked: false,
          column: null,
          label: 'Sum',
        },
        header: {
          checked: false,
          color: '#F5F6F8',
          rgb: { r: 245, g: 246, b: 248 },
        },
        tax: {
          checked: false,
          label: 'Tax',
          type: null,
          value: '',
        },
        currency: {
          checked: false,
          type: null,
          position: { value: 'before', label: t('Before the value') },
          column: [],
        },
        columns:
          data?.[0]?.column_values
            ?.filter((x) => supportedColumnsFromMonday.includes(x.type))
            .slice(0, 4)
            ?.map((col) => ({
              ...col,
              label: col.title || col.column.title,
              checked: false,
            })) || [],
        columnWidth: columnWidth,
        totalSum: 0,
        itemNameColumn: {
          checked: true,
          label: 'Item',
        },
      };

      setSubItems(defaultVal);
    },
    [setSubItems],
  );

  const saveSignatures = useCallback(
    (id, cb, isVersion6 = true, fileHistoryId) => {
      if (signFlowData?.signedFiles?.length) {
        addSignature(
          {
            itemId,
            fileHistoryId,
            body: {
              itemId,
              status: isSenderSigningPage
                ? 'signed_by_sender'
                : 'signed_by_receiver',
              signed_files: signFlowData?.signedFiles,
            },
          },
          {
            onSuccess: (res) => {
              cb?.(res);
            },
          },
        );
      } else {
        cb?.();
      }
    },
    [addSignature, addedFormFields, isSenderSigningPage, itemId],
  );

  const updateActiveHeaderButton = useCallback(
    (val) => {
      setActiveHeaderButton(val);
    },
    [setActiveHeaderButton],
  );

  const updateActiveHeaderButtonSignAnywhere = useCallback(
    (val) => {
      setActiveHeaderButtonSignAnywhere(val);
    },
    [setActiveHeaderButtonSignAnywhere],
  );

  const handleFontIncrease = useCallback(() => {
    const idx = currentSelectedItem?.current;
    if (idx > -1) {
      const currentField = addedFormFields[idx];
      if (!currentField?.fontSize) {
        currentField.fontSize = 12;
        currentField.fontStyle.fontSize = 12;
      } else {
        currentField.fontSize += 1;
        currentField.fontStyle.fontSize += 1;
      }

      setAddedFormFields([...addedFormFields]);
    }
  }, [setAddedFormFields, addedFormFields]);

  const handleFontDecrease = useCallback(() => {
    const idx = currentSelectedItem?.current;
    if (idx > -1) {
      const currentField = addedFormFields[idx];
      if (!currentField?.fontSize) {
        currentField.fontSize = 10;
        currentField.fontStyle.fontSize = 10;
      } else {
        currentField.fontSize -= 1;
        currentField.fontStyle.fontSize -= 1;
      }

      setAddedFormFields([...addedFormFields]);
    }
  }, [setAddedFormFields, addedFormFields]);

  const setSigningPage = useCallback(({ sender, receiver }) => {
    if (sender) {
      setIsSenderSigningPage(true);
      return;
    }

    setIsReceiverSigningPage(true);
    return;
  }, []);

  const enableSignaturePad = useCallback(({ show, item, action }) => {
    setShowSignaturePad({
      show,
      item,
      action,
    });
  }, []);

  const updateIsDeleted = useCallback(
    (value) => {
      setIsDeleted(value);
    },
    [setIsDeleted],
  );

  const updateIsCancelled = useCallback(
    (value) => {
      setIsCancelled(value);
    },
    [setIsCancelled],
  );

  const updateFileId = useCallback(
    (id) => {
      setFileId(id);
    },
    [setFileId],
  );

  const updateEnvelopeId = useCallback(
    (id) => {
      setEnvelopeId(id);
    },
    [setEnvelopeId],
  );

  const onDocumentLoadSuccess = useCallback(async (page) => {
    setNumPages(page.numPages);
  }, []);

  const updateBoardId = useCallback(
    (value) => {
      setBoardId(value);
    },
    [setBoardId],
  );

  const updateItemId = useCallback(
    (value) => {
      setItemId(value);
    },
    [setItemId],
  );

  const updateFormFields = useCallback(
    (fields) => {
      // setAddedFormFieldsChanged(true);
      setAddedFormFields(Array.isArray(fields) ? [...fields] : []);
    },
    [setAddedFormFields],
  );

  const setPDFFile = useCallback(
    (file) => {
      setFile(file?.file);
    },
    [setFile],
  );

  useEffect(() => {
    async function getPDFHeight(file) {
      const pdfDoc = await PDFDocument.load(file);
      const pages = pdfDoc.getPages();

      const pageDetails = pages.map((x) => {
        const dimension = x.getSize();
        const angle = x.getRotation().angle;
        let updatedDimension = dimension;
        if (angle === 90 || angle === 270) {
          updatedDimension = {
            width: dimension.height,
            height: dimension.width,
          };
        }
        return updatedDimension;
      });

      const firstPage = pageDetails[0];
      setPdfDefaultHeight(firstPage.height);

      setPdfIndividualPageDetails(pageDetails);
    }
    if (envelopeData?.activeFile?.file) {
      getPDFHeight(envelopeData?.activeFile?.file);
    }
    if (signFlowData?.activeFile?.file) {
      getPDFHeight(signFlowData?.activeFile?.file);
    }
    if (previewFlowData?.activeFile?.file) {
      getPDFHeight(previewFlowData?.activeFile?.file);
    }
    if (generateFlowData?.pdfFileData?.updated_file) {
      getPDFHeight(generateFlowData?.pdfFileData?.updated_file);
    }
  }, [
    envelopeData?.activeFile?.file,
    signFlowData?.activeFile?.file,
    previewFlowData?.activeFile?.file,
    generateFlowData?.pdfFileData?.updated_file,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChangedAddedFormFields) {
        // Display the confirmation message only if the form has changed
        const confirmationMessage = 'Are you sure you want to leave this page?';
        e.returnValue = confirmationMessage;
        return confirmationMessage;
      }
    };
    if (hasChangedAddedFormFields) {
      // Add the beforeunload event listener when the form has changed
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      // Remove the event listener when the form has not changed
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChangedAddedFormFields]);

  // remove any signer which is not associated with any fields in pdf
  useEffect(() => {
    if (updateSignOrderList) {
      removeAnyUnusedSigners();
      setUpdateSignOrderList(false);
    }
  }, [updateSignOrderList]);

  useEffect(() => {
    const canvas = documentRef?.current?.querySelector('.react-pdf__Page');
    if (!canvas || !pdfDefaultHeight) return;
    const canvasRect = canvas.getBoundingClientRect();
    setPdfDimensions({
      height: canvasRect.height,
      width: canvasRect.width,
      scale: canvasRect.height / pdfDefaultHeight,
    });
  }, [pdfDefaultHeight, hasPageLoaded, pageScale, hasPreviewPageLoaded]);

  return (
    <FileContext.Provider
      value={{
        file: eachFile,
        itemId,
        fileId,
        userId,
        boardId,
        numPages,
        subItems,
        boardData,
        isDeleted,
        boardName,
        pageScale,
        headerData,
        envelopeId,
        documentRef,
        pdfUpdating,
        isCancelled,
        activeField,
        previewFile,
        envelopeData,
        toastMessage,
        signFlowData,
        isInitialLoad,
        pdfDimensions,
        initialFields,
        hasPageLoaded,
        signatureMode,
        isItemDragging,
        editorFlowData,
        animateTooltip,
        columnValuesMap,
        signersSettings,
        previewFlowData,
        addedFormFields,
        pageDeleteIndex,
        pagesAddedByUser,
        generateFlowData,
        pdfDefaultHeight,
        showSignaturePad,
        showLineItemModal,
        activeHeaderButton,
        imagesToRemoveArray,
        isSenderSigningPage,
        draggableFieldsData,
        currentSelectedItem,
        triggerHeaderButton,
        successToastMessage,
        isEditorSaveLoading,
        lineItemsRowHeights,
        isPreviewSaveLoading,
        hasPreviewPageLoaded,
        isReceiverSigningPage,
        displayOnboardingFlow,
        showSignatureOrderModal,
        isLoading: isAddingSign,
        pdfIndividualPageDetails,
        showCKEditorEntryConfirmation,
        activeHeaderButtonSignAnywhere,
        isPreviewLoading: isPreviewLoading || isPreviewFetching,
        isAdhocWithSignatureFieldAutoAssign,
        setUserId,
        setPDFFile,
        setNumPages,
        updateFileId,
        downloadFile,
        updateItemId,
        setBoardName,
        setBoardData,
        triggerToast,
        setPageScale,
        updateBoardId,
        updatePdfInBE,
        setActiveField,
        updateSubItems,
        addPlaceHolder,
        addPlaceholderForAcroFields,
        setSigningPage,
        setPreviewFile,
        saveSignatures,
        setSignFlowData,
        updateIsDeleted,
        scrollDocumentY,
        resizeSignature,
        setEnvelopeData,
        setHasPageLoaded,
        setIsInitialLoad,
        uploadUpdatedPDF,
        updateFormFields,
        setSignatureMode,
        setInitialFields,
        updateEnvelopeId,
        updatePreviewFile,
        addSignatureImage,
        updateIsCancelled,
        removePlaceholder,
        setIsItemDragging,
        setEditorFlowData,
        setPreviewFlowData,
        handleFontDecrease,
        setPageDeleteIndex,
        handleFontIncrease,
        setColumnValuesMap,
        enableSignaturePad,
        setSignersSettings,
        setGenerateFlowData,
        setPagesAddedByUser,
        handleEditSignature,
        handleRenderSuccess,
        triggerSuccessToast,
        setShowLineItemModal,
        handleAnimateTooltip,
        handleSaveFormFields,
        onDocumentLoadSuccess,
        handleDeleteSignature,
        setUpdateSignOrderList,
        handleTemplateFieldsV3,
        setIsEditorSaveLoading,
        setTriggerHeaderButton,
        setDraggableFieldsData,
        validateSaveFormFields,
        setHasPreviewPageLoaded,
        setIsPreviewSaveLoading,
        setDisplayOnboardingFlow,
        setShowSignateOrderModal,
        updateActiveHeaderButton,
        updateLineItemsRowHeights,
        setAddedFormFieldsChanged,
        addSignatureFieldInSignPage,
        setShowCKEditorEntryConfirmation,
        updateActiveHeaderButtonSignAnywhere,
        setIsAdhocWithSignatureFieldAutoAssign,
        paymentFieldAdded,
        setPaymentFieldAdded,
        pricingPlan,
        setPricingPlan,
        setSlug,
        slug,
        setIsTemplate,
        isTemplate,
        setUpdateTemplateAndFileFields,
        handleSaveFormFieldsV3,
        setImagesToRemoveArray,
      }}
    >
      {children}
    </FileContext.Provider>
  );
};
